import React, { useState, ChangeEvent, FormEvent } from 'react';

interface FormProps {
  onSubmit: (values: any) => void;
  initialValues: Record<string, any>;
  children: React.ReactNode;
}

export function FormBase({ onSubmit, initialValues, children }: FormProps) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  const setValue = (name: string, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const setError = (name: string, error: string) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const clearError = (name: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  // Clone children and inject props
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement, {
        values,
        errors,
        setValue,
        setError,
        clearError,
      });
    }
    return child;
  });

  return <form onSubmit={handleSubmit}>{childrenWithProps}</form>;
}

interface FormFieldProps {
  name: string;
  label?: string;
  values?: Record<string, any>;
  errors?: Record<string, string>;
  setValue?: (name: string, value: any) => void;
  setError?: (name: string, error: string) => void;
  clearError?: (name: string) => void;
  validate?: (value: any) => string | null;
  children: React.ReactNode;
}

export function FormField({
  name,
  label,
  values = {},
  errors = {},
  setValue,
  setError,
  clearError,
  validate,
  children,
}: FormFieldProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue?.(name, value);

    if (validate) {
      const error = validate(value);
      if (error) {
        setError?.(name, error);
      } else {
        clearError?.(name);
      }
    }
  };

  // Clone the child (input) and inject props
  const childWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement, {
        name,
        value: values[name] || '',
        onChange: handleChange,
        'aria-invalid': errors[name] ? 'true' : 'false',
      });
    }
    return child;
  });

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}
      {childWithProps}
      {errors[name] && (
        <div className="text-red-500 text-sm">{errors[name]}</div>
      )}
    </div>
  );
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
}

export function Input({ className = '', ...props }: InputProps) {
  return (
    <input
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${className}`}
      {...props}
    />
  );
}
